import * as React from 'react';
import { Link } from 'react-router-dom';
import { Translate } from '../../shared/LanguageService';
import * as Features from '../../shared/Features';
import { IOrderListItem, IStatus, ProductProperties } from './Entities';
import moment from 'moment';
import * as Site from '../../shared/Site';
import * as PlanOrderCombinedStatus from './view/planOrders/PlanOrderCombinedStatus';
import { isBrokerAgent, getBrokerAgentPhotoStatus } from './utils/brokerAgentUtils';
import { isPhotoOrder } from './utils/orderUtils';
import { TranslatedLabels } from '../../shared/types';
import Button from '@mui/material/Button';
import EditIcon from '@mui/icons-material/Edit';
import { TableRow, TableCell, Tooltip } from '@mui/material';
import ThreeDRotationIcon from '@mui/icons-material/ThreeDRotation';
import VideocamIcon from '@mui/icons-material/Videocam';
import StraightenIcon from '@mui/icons-material/Straighten';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import {Laptop} from "@mui/icons-material";
import drone from '../../assets/images/drone-icon.svg';

interface IOrderListRowProps {
    order: IOrderListItem;
    user: any;
    navigateTo: (route: string) => void;
    isNewOrder: boolean;
    includeAddress?: boolean;
    labels?: any;
}

type OrderStatusProps = {
    orderType: number;
    orderStatus: IStatus;
    userRole: string;
};

type PhotoOrderStatusProps = {
    orderStatus: IStatus;
    userRole: string;
    labels: TranslatedLabels;
};

type TranslatedPhotoOrderStatusProps = {
    orderStatus: IStatus;
    userRole: string;
};

function PhotoOrderStatus({ orderStatus, userRole, labels }: PhotoOrderStatusProps) {
    const userIsBrokerAgent = isBrokerAgent(userRole);
    const photoStatus = userIsBrokerAgent
        ? getBrokerAgentPhotoStatus(orderStatus.displayName, labels.customerPhotoStatus)
        : Translate(orderStatus.displayName);
    return <>{photoStatus}</>;
}

function TranslatedPhotoOrderStatus({ orderStatus, userRole }: TranslatedPhotoOrderStatusProps) {
    const labels = {
        customerPhotoStatus: Translate('photo-order/status/customer-under-production')
    };
    return <PhotoOrderStatus orderStatus={orderStatus} userRole={userRole} labels={labels} />;
}

function OrderStatus({ orderType, orderStatus, userRole }: OrderStatusProps) {
    if (isPhotoOrder(orderType)) {
        return <TranslatedPhotoOrderStatus orderStatus={orderStatus} userRole={userRole} />;
    }

    return <>{Translate(orderStatus.displayName)}</>;
}

function propertyIcon(icon, label, display) {
    if (display) {
        return <div style={{ display: "flex" }}>
            <Tooltip title={label} style={{ marginRight: "6px", color: "#333D45" }}>
                {icon}
            </Tooltip>
        </div> 
    }
}

function OrderListRow(props: IOrderListRowProps) {
    function displayLegacyOrderNumber() {
        if (
            props.order.legacyOrderNumber != null &&
            props.order.orderCollectionNumber.indexOf(props.order.legacyOrderNumber) === -1
        ) {
            return <span> ({props.order.legacyOrderNumber})</span>;
        }
    }

    function displayOrderIcon(order) {
        if (order.orderType.displayName === 'photo') {
            return (
                <i
                    className="material-icons md-24 color-info"
                    title={Translate(props.order.orderType.translateKey)}
                >
                    photo_camera
                </i>
            );
        }
        if (order.orderType.displayName === 'plan') {
            return (
                <i
                    className="material-icons md-24 color-info"
                    title={Translate(props.order.orderType.translateKey)}
                >
                    map
                </i>
            );
        }
        if (order.orderType.displayName === 'styling') {
            return (
                <i
                    className="material-icons md-24 color-info"
                    title={Translate(props.order.orderType.translateKey)}
                >
                    style
                </i>
            );
        }
        if (order.orderType.displayName === 'showcase'){
            return (
                <i className="material-icons color-info"><Laptop/></i>
            );
        }
        return (
            <i
                className={`icon-generic-order md-24 color-info`}
                title={Translate(props.order.orderType.translateKey)}
            />
        );
    }

    function containProperty(property) {
        if (props.order.productProperties)
            return Boolean(props.order.productProperties.find(productProperty => productProperty.value === property));
    }

    let rowClass = '';
    if (props.order.isExpress) {
        rowClass = 'express';
    } else if (props.order.isExpressWeekend) {
        rowClass = 'express-weekend';
    } else if (props.order.isEarlyDelivery) {
        rowClass = 'early-delivery';
    }

    if (props.isNewOrder) {
        rowClass += ' new-order';
    }

    const buttonStyle = { 
        backgroundColor: "#484e54", 
        "&:hover": { backgroundColor: "#383e44" },
        textTransform: 'none',
        color: "white", 
        boxShadow: `
            0px 3px 1px -2px rgba(0, 0, 0, 0.2), 
            0px 2px 2px rgba(0, 0, 0, 0.14), 
            0px 1px 5px rgba(0, 0, 0, 0.12)
        `,
        margin: "8px"
    };

    const { labels } = props;

    return (
        <TableRow className={rowClass}>
            <TableCell>
                <Link to={`/ordercollections/${props.order.orderCollectionId}`}>
                    {props.order.orderCollectionNumber}
                </Link>
                :{props.order.orderNumber}
                {displayLegacyOrderNumber()}{displayOrderIcon(props.order)}
            </TableCell>
            <TableCell sx={{ fontWeight: "400" }}>
                {moment(props.order.orderPlacedAt).format('L LT')}
            </TableCell>
            <TableCell className="hidden-xs" sx={{ fontWeight: "400" }}>
                {props.order.bookingStart ? moment(props.order.bookingStart).format('L LT') : ''}
            </TableCell>
            <TableCell className="hidden-xs" sx={{ fontWeight: "400" }}>
                {(props.order.site === Site.SE360 ||
                    Features.hasFeature(
                        Features.ViewBookedScandphotoPhotographerInfoRegardlessTime,
                        props.user
                    ) ||
                    (props.order.site === Site.Scandphotos &&
                        moment(props.order.bookingStart).diff(moment(), 'hours', true) < 24)) &&
                    props.order.photographer}
            </TableCell>
            <TableCell className="hidden-xs" sx={{ fontWeight: "400" }}>
                {props.order.packageProduct}
            </TableCell>
            <TableCell className="hidden-sm hidden-xs" style={{ alignItems: "center" }}>
                <div style={{ display: "flex" }}>
                    {propertyIcon(<ThreeDRotationIcon />, labels.matterport, containProperty(ProductProperties.Matterport))}
                    {propertyIcon(<VideocamIcon />, labels.video, containProperty(ProductProperties.Video))}
                    {propertyIcon(<StraightenIcon />, labels.measuring, containProperty(ProductProperties.Measuring))}
                    {propertyIcon(<TextFieldsIcon />, labels.copywriting, containProperty(ProductProperties.Text))}
                    {propertyIcon(<FlashOnIcon />, labels.express, (props.order.isExpress || props.order.isExpressWeekend))}
                    {containProperty(ProductProperties.Drone) && <div style={{ display: "flex" }}>
                        <Tooltip title={props.labels.drone}>
                            <img src={drone} title={labels.drone} style={{ marginRight: "6px" }} />
                        </Tooltip>
                    </div>}
                </div>
            </TableCell>
            {props.includeAddress && props.order.city && (
                <TableCell sx={{ fontWeight: "400" }}>
                    {props.order.address}, {props.order.city}
                </TableCell>
            )}
            {props.includeAddress && !props.order.city && <TableCell>{props.order.address}</TableCell>}
            <TableCell sx={{ fontWeight: "400" }}>
                {props.order.isArchived && props.order.archivedStatus !== 3
                ? labels.archived
                : props.order.customerDeliveryAnnotation &&
                    props.order.status.value === PlanOrderCombinedStatus.Delivered &&
                    isBrokerAgent(props.user.details.role) ? (
                        <span>{labels.notReturnedAnnotations}</span>
                    ) : props.order && props.order.status && props.order.orderType ? (
                        <OrderStatus
                            orderType={props.order.orderType.value}
                            orderStatus={props.order.status}
                            userRole={props.user.details.role}
                        />
                    ) : null
                }
            </TableCell>
            <TableCell className="tools" sx={{ fontWeight: "400" }}>
                {Features.hasFeature(Features.ViewOrder, props.user) && (
                    <Button 
                        sx={buttonStyle} 
                        size="small"
                        onClick={() => window.location.href = `/orders/${props.order.id}`}
                        startIcon={<EditIcon />}
                    >
                        {labels.viewButtonLabel}
                    </Button>
                )}
            </TableCell>
        </TableRow>
    );
}

const OrderListRowTranslationLayer = (props): any => {
    const labels = {
        matterport: Translate("product-property/matterport"),
        key: Translate("product-property/key"),
        video: Translate("product-property/film"),
        measuring: Translate("product-property/measured"),
        copywriting: Translate("product-property/text"),
        express: Translate("product-property/express"),
        drone: Translate("product-property/drone"),
        viewButtonLabel: Translate('orders/list/row/button-label/view'),
        notReturnedAnnotations: Translate('view-order/basic-order-info/not-returned-annotations'),
        archived: Translate('photo-order/status/archived')
    };
    return <OrderListRow {...props} labels={labels} />;
};

export default OrderListRowTranslationLayer;
